import React, { useMemo, useState } from "react";
import { ButtonControl, FormFieldsControl, IconControl } from "spark-core-dx/components";

/**
 * MiniTableControl
 * Small editable table with per-row detail editor.
 *
 * Props:
 * - title?: string
 * - rows: any[]
 * - columns: { key: string, title: string }[]
 * - editable?: boolean = true
 * - onRowsChange?: (next: any[]) => void
 *
 * - rowKey?: string   // defaults to "_id" then "Name" then index
 * - columnFieldMetadata?: any // OPTIONAL — if provided, will render FormFieldsControl
 *                              // in detail mode for the selected row.
 *
 * Behavior:
 * - Inline edits for visible columns (text inputs by default).
 *   (NOTE: If you have enumerations for Format/DataTypeName, you can
 *    enhance this with dropdowns — I left them as inputs intentionally.)
 * - Per-row actions: Edit Details (toggle), Delete
 * - Add Row button (only if editable)
 */
const MiniTableControl = ({
  title,
  rows,
  columns,
  editable = true,
  onRowsChange,
  rowKey,
  columnFieldMetadata, // if you can supply nested column metadata, details editor uses FormFieldsControl
}) => {
  const [expanded, setExpanded] = useState(null); // rowKey or index

  const getKey = (row, i) => {
    if (rowKey && row && row[rowKey] != null) return row[rowKey];
    if (row && row._id) return row._id;
    if (row && row.Name) return row.Name + ":" + i;
    return i;
  };

  const sorted = useMemo(() => {
    const copy = Array.isArray(rows) ? [...rows] : [];
    // If a “Sequence” column exists, keep rows roughly sorted by sequence for visual consistency
    return copy.sort((a, b) => (a?.Sequence ?? 0) - (b?.Sequence ?? 0));
  }, [rows]);

  const updateCell = (rowIndex, key, value) => {
    if (!onRowsChange) return;
    const next = [...sorted];
    const realIndex = rows.indexOf(sorted[rowIndex]); // map back to original index
    const updatedRow = { ...(rows[realIndex] || {}), [key]: value };
    const out = [...rows];
    out[realIndex] = updatedRow;
    onRowsChange(out);
  };

  const deleteRow = (rowIndex) => {
    if (!onRowsChange) return;
    const realIndex = rows.indexOf(sorted[rowIndex]);
    const out = rows.filter((_, i) => i !== realIndex);
    onRowsChange(out);
  };

  const addRow = () => {
    if (!onRowsChange) return;
    const nextSeq = (rows?.reduce?.((m, r) => Math.max(m, r?.Sequence ?? 0), 0) ?? 0) + 1;
    onRowsChange([...(rows || []), { Name: "", DisplayName: "", Format: "", DataTypeName: "", Sequence: nextSeq }]);
  };

  const toggleDetails = (rowIndex) => {
    const k = getKey(sorted[rowIndex], rowIndex);
    setExpanded((prev) => (prev === k ? null : k));
  };

  const updateRowViaDetails = (rowIndex, patch) => {
    if (!onRowsChange) return;
    const realIndex = rows.indexOf(sorted[rowIndex]);
    const updatedRow = { ...(rows[realIndex] || {}), ...patch };
    const out = [...rows];
    out[realIndex] = updatedRow;
    onRowsChange(out);
  };

  return (
    <div className="mini-table">
      {title && <div className="mini-table__title"><h4>{title}</h4></div>}

      <div className="mini-table__header">
        <div className="mini-table__cols">
          {columns.map((c) => (
            <div key={c.key} className="mini-table__col mini-table__col--head">{c.title}</div>
          ))}
          <div className="mini-table__col mini-table__col--head mini-table__col--actions">Actions</div>
        </div>
      </div>

      <div className="mini-table__body">
        {sorted.length === 0 && <div className="muted">No rows.</div>}

        {sorted.map((row, rIdx) => {
          const k = getKey(row, rIdx);
          const isExpanded = expanded === k;
          return (
            <div key={k} className="mini-table__rowwrap">
              <div className="mini-table__row">
                {columns.map((c) => (
                  <div key={c.key} className="mini-table__col">
                    {!editable ? (
                      <div className="mini-table__value">{row?.[c.key] ?? ""}</div>
                    ) : (
                      <input
                        className="mini-table__input"
                        value={row?.[c.key] ?? ""}
                        onChange={(e) => updateCell(rIdx, c.key, e.target.value)}
                        placeholder={c.title}
                      />
                    )}
                  </div>
                ))}

                <div className="mini-table__col mini-table__col--actions">
                  <ButtonControl
                    type="link"
                    onClick={() => toggleDetails(rIdx)}
                    title={isExpanded ? "Hide Details" : "Edit Details"}
                  >
                    {isExpanded ? "Hide" : "Edit"}
                  </ButtonControl>
                  {editable && (
                    <IconControl iconKey="trash" title="Delete" onClick={() => deleteRow(rIdx)} />
                  )}
                </div>
              </div>

              {/* Details editor (collapsible) */}
              {isExpanded && (
                <div className="mini-table__details">
                  {columnFieldMetadata ? (
                    <FormFieldsControl
                      metadata={columnFieldMetadata}
                      item={row}
                      // NOTE: If you want to restrict/edit specific fields in details:
                      // fieldNames={...}
                      onChange={(value, fieldName) => updateRowViaDetails(rIdx, { [fieldName]: value })}
                    />
                  ) : (
                    <div className="mini-table__details-raw">
                      {/* Fallback simple JSON-ish editor when no metadata is available */}
                      <div className="hint">
                        {/* TODO: Pass columnFieldMetadata for a full-featured row editor */}
                        No column metadata provided. Showing raw JSON editor for this row.
                      </div>
                      <textarea
                        className="mini-table__json"
                        rows={8}
                        spellCheck={false}
                        value={JSON.stringify(row ?? {}, null, 2)}
                        onChange={(e) => {
                          try {
                            const parsed = JSON.parse(e.target.value);
                            updateRowViaDetails(rIdx, parsed);
                          } catch {
                            // ignore until valid JSON
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {editable && (
        <div className="mini-table__footer">
          <ButtonControl type="ok-small" onClick={addRow}>Add Row</ButtonControl>
        </div>
      )}
    </div>
  );
};

export default MiniTableControl;
